import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Clock, Settings, Trash2, Timer, Calendar , Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps } from '@/types/flow';

const DelayNode = ({ data, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const delaySeconds = config.delay_seconds || 0;

    const formatDelay = (seconds: number) => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (days > 0) return { value: days, unit: days === 1 ? 'dia' : 'dias', icon: Calendar };
        if (hours > 0) return { value: hours, unit: hours === 1 ? 'hora' : 'horas', icon: Clock };
        if (minutes > 0) return { value: minutes, unit: minutes === 1 ? 'minuto' : 'minutos', icon: Clock };
        return { value: secs, unit: secs === 1 ? 'segundo' : 'segundos', icon: Timer };
    };

    const timeInfo = formatDelay(delaySeconds);
    const TimeIcon = timeInfo.icon;

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-amber-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
                        <Clock className="w-3.5 h-3.5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Aguardar'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Atraso</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-3">
                {delaySeconds > 0 ? (
                    <div className="flex items-center justify-center gap-3 py-2">
                        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-100">
                            <TimeIcon className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-gray-800 leading-none">{timeInfo.value}</div>
                            <div className="text-[11px] text-gray-500 mt-0.5">{timeInfo.unit}</div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-3">
                        <p className="text-xs text-gray-400">Tempo não configurado</p>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-amber-600 hover:bg-amber-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <Handle type="target" id="target" position={Position.Left} className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            <Handle type="source" id="source" position={Position.Right} className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
    );
};

export default memo(DelayNode);
